import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import Pagination, { paginate } from "@/components/Pagination";

const empty = { nom: "", telephone: "", email: "", adresse: "", notes: "" };

export default function Proprietaires() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [page, setPage] = useState(1);

  const pag = paginate(items, page);

  const load = async () => {
    const { data } = await api.get("/proprietaires");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/proprietaires/${editing}`, form);
        toast.success("Propriétaire modifié");
      } else {
        await api.post("/proprietaires", form);
        toast.success("Propriétaire créé");
      }
      setOpen(false); setForm(empty); setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    }
  };

  const onEdit = (it) => { setEditing(it.id); setForm({ nom: it.nom, telephone: it.telephone, email: it.email, adresse: it.adresse, notes: it.notes }); setOpen(true); };
  const onDelete = async () => {
    try {
      await api.delete(`/proprietaires/${confirmId}`);
      toast.success("Propriétaire supprimé");
      setConfirmId(null);
      load();
    } catch (e) { toast.error("Suppression échouée"); }
  };

  return (
    <div>
      <PageHeader
        title="Propriétaires"
        subtitle="Gérez la liste de vos bailleurs et leurs informations de contact."
        testid="page-proprietaires"
        accent="amber"
        actions={
          <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-orange-500 hover:bg-orange-600 text-white shadow-md shadow-orange-900/20" data-testid="btn-add-proprietaire">
            <Plus className="w-4 h-4 mr-2" /> Nouveau
          </Button>
        }
      />

      <Card className="rounded-xl border border-slate-200 shadow-sm overflow-hidden bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Nom</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Téléphone</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Adresse</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-12">
                <Users className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                Aucun propriétaire enregistré.
              </TableCell></TableRow>
            )}
            {pag.rows.map((p) => (
              <TableRow key={p.id} className="hover:bg-slate-50/60" data-testid={`row-proprio-${p.id}`}>
                <TableCell className="font-medium text-slate-900">{p.nom}</TableCell>
                <TableCell className="text-slate-600">{p.telephone || "—"}</TableCell>
                <TableCell className="text-slate-600">{p.email || "—"}</TableCell>
                <TableCell className="text-slate-600">{p.adresse || "—"}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => onEdit(p)} data-testid={`btn-edit-proprio-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmId(p.id)} data-testid={`btn-delete-proprio-${p.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Pagination {...pag} onChange={setPage} testid="pagination-proprios" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="dialog-proprietaire">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Modifier propriétaire" : "Nouveau propriétaire"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nom complet *</Label>
              <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} required data-testid="input-proprio-nom" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} data-testid="input-proprio-tel" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="input-proprio-email" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} data-testid="input-proprio-adresse" />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} data-testid="input-proprio-notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-proprio">Enregistrer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="btn-confirm-delete">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
